import React, {
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  RefObject,
  createRef,
  ComponentRef,
} from 'react';
import {
  Box,
  Breakpoint,
  ScrollView,
  Text,
  useBreakpoint,
} from '@bonanza-social/moonlight';
import { Trans } from '@bonanza-social/i18n';
import RegistrationField from '../../RegistrationField/RegistrationField';
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  Easing,
} from 'react-native-reanimated';

import { RegistrationStepProps } from '../RegistrationStep.types';
import { useNavigation } from '@bonanza-social/navigation';
import {
  selectFormErrorsByFields,
  selectRegistrationConfig,
  selectRegistrationConfigVariant,
  selectRegistrationFormData,
  selectRegistrationStepIndex,
  setStepIndex,
  updateRegistrationData,
} from '../../../state';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { Dimensions, TextInput, LayoutChangeEvent } from 'react-native';
import DefaultRegistrationStepButton from './DefaultRegistrationStepButton.web';

const { height: screenHeight } = Dimensions.get('window');

function DefaultRegistrationStep({
  step,
  onSubmit: onSubmitProp,
  submitDataState,
}: RegistrationStepProps) {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();
  const [showErrors, setShowErrors] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const scrollViewRef = useRef<ComponentRef<typeof ScrollView>>(null);
  const config = useSelector(selectRegistrationConfig);
  const data = useSelector(selectRegistrationFormData);
  const errors = useSelector(selectFormErrorsByFields(step.fields || []));
  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  const [hasScrolledForErrors, setHasScrolledForErrors] = useState(false);
  const stepIndex = useSelector(selectRegistrationStepIndex);
  const [buttonContainerHeight, setButtonContainerHeight] = useState(0);
  const variant = useSelector(selectRegistrationConfigVariant);
  const isMobile = breakpoint === Breakpoint.MOBILE;
  const isButtonInline =
    variant === '3Step' && (step.id === 'verify' || step.id === 'complete');
  const fieldOffsets = useRef<Record<string, number>>({});

  const opacity = useSharedValue(0);
  const translateY = useSharedValue(50);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    };
  });

  const buttonTopPosition = useMemo(() => {
    return contentHeight - 10;
  }, [contentHeight]);

  const handleButtonContainerLayout = useCallback(() => {
    setButtonContainerHeight(220);
  }, []);

  const shouldUseAbsolutePositioning = useMemo(() => {
    return (
      contentHeight + buttonContainerHeight + 120 < screenHeight && !isMobile
    );
  }, [contentHeight, buttonContainerHeight, isMobile]);

  const buttonContainerStyle = useMemo(
    () => ({
      ...(shouldUseAbsolutePositioning
        ? {
            position: 'absolute' as const,
            top: buttonTopPosition,
          }
        : {}),
      left: 0,
      right: 0,
      alignItems: 'center' as const,
      backgroundColor: 'transparent',
    }),
    [shouldUseAbsolutePositioning, buttonTopPosition],
  );

  const onSubmit = useCallback(() => {
    if (hasErrors) {
      setShowErrors(true);
      setHasScrolledForErrors(false);
      return;
    }

    onSubmitProp();
  }, [hasErrors, onSubmitProp]);

  const handleContentLayoutCustom = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setContentHeight(height);
  }, []);

  useEffect(() => {
    if (!(showErrors && hasErrors && !hasScrolledForErrors)) return;

    const id = setTimeout(() => {
      const fields = step.fields ?? [];
      const firstErrored = fields.find(
        (f) => !!(errors as Record<string, string | undefined>)[f],
      );
      if (!firstErrored) return;

      const y = fieldOffsets.current[firstErrored];
      if (typeof y !== 'number') return;

      const TOP_PADDING = 100;

      scrollViewRef.current?.scrollTo({
        y: Math.max(y - TOP_PADDING, 0),
        animated: true,
      });

      const idx = fields.indexOf(firstErrored);
      requestAnimationFrame(() => {
        fieldRefs.current[idx]?.current?.focus?.();
      });

      setHasScrolledForErrors(true);
    }, 50);

    return () => clearTimeout(id);
  }, [showErrors, hasErrors, hasScrolledForErrors, errors, step.fields]);

  useEffect(() => {
    opacity.value = 0;
    translateY.value = 50;

    opacity.value = withTiming(1, {
      duration: 200,
      easing: Easing.out(Easing.ease),
    });
    translateY.value = withTiming(0, {
      duration: 200,
      easing: Easing.out(Easing.ease),
    });
  }, [step.id, opacity, translateY]);

  const fieldRefs = useRef<{ [key: number]: RefObject<TextInput> }>(
    step?.fields?.reduce(
      (acc, _, idx) => ({
        ...acc,
        [idx]: createRef<TextInput>(),
      }),
      {},
    ) || {},
  );

  const buttonProps = useMemo(
    () => ({
      containerStyle: buttonContainerStyle,
      onLayout: handleButtonContainerLayout,
      animatedStyle: animatedStyles,
      onSubmit,
      ctaKey: step.cta,
      loading: submitDataState === 'pending',
      disabled: showErrors && hasErrors,
      featureTip: step.featureTip,
    }),
    [
      buttonContainerStyle,
      handleButtonContainerLayout,
      animatedStyles,
      onSubmit,
      step.cta,
      submitDataState,
      showErrors,
      hasErrors,
      step.featureTip,
    ],
  );

  const ContinueButton = <DefaultRegistrationStepButton {...buttonProps} />;

  return (
    <Box flex={1} width="100%" testID={`registration-step-${step.id}`}>
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        animated={true}
        style={{ flex: 1 }}
      >
        <Box alignItems="center" width="100%">
          <Box
            width={{
              mobile: '100%',
              tablet: '100%',
              desktop: 760,
            }}
          >
            <Box
              padding="space-400"
              animatedStyle={animatedStyles}
              onLayout={handleContentLayoutCustom}
            >
              <Box marginBottom="space-100" marginTop="space-300">
                {step.stepTitleNS && step.stepTitleKey ? (
                  <Text
                    variant="h5"
                    color="darkSurfacePrimary"
                    locNS={step.stepTitleNS}
                    locKey={step.stepTitleKey}
                    marginBottom="space-300"
                  />
                ) : null}
                {step.stepDescriptionNS && step.stepDescriptionKey ? (
                  <Text
                    variant="body-m"
                    color="darkSurfacePrimary"
                    marginBottom="space-300"
                  >
                    <Trans
                      i18nKey={step.stepDescriptionKey}
                      ns={step.stepDescriptionNS}
                    />
                  </Text>
                ) : null}
              </Box>
              <Box width="100%">
                {step.fields?.map((field, index) => (
                  <Box
                    key={field}
                    onLayout={(e) => {
                      fieldOffsets.current[field] = e.nativeEvent.layout.y;
                    }}
                    marginBottom="space-400"
                  >
                    <RegistrationField
                      name={field}
                      autoFocus={index === 0}
                      ref={fieldRefs.current[index]}
                      enterKeyHint={
                        index < (step.fields?.length ?? 1) - 1 ? 'next' : 'go'
                      }
                      onSubmitEditing={() => {
                        if (step.fields && index < step.fields.length - 1) {
                          fieldRefs.current[index + 1]?.current?.focus();
                        } else {
                          if (hasErrors) {
                            setShowErrors(true);
                            setHasScrolledForErrors(false);
                            return;
                          }
                          dispatch(setStepIndex(stepIndex + 1));
                        }
                      }}
                      {...config.fields[field]}
                      value={data[field as keyof typeof data]}
                      error={
                        showErrors
                          ? errors[field as keyof typeof errors]
                          : undefined
                      }
                      onValue={(singleField, multipleFields) => {
                        dispatch(
                          updateRegistrationData(
                            multipleFields ?? { [field]: singleField },
                          ),
                        );
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        {isButtonInline ? ContinueButton : null}
      </ScrollView>
      {!isButtonInline ? ContinueButton : null}
    </Box>
  );
}

export default memo(DefaultRegistrationStep);
